import React, { useEffect, useState } from "react";
import { adminGetOnchain, adminSetOnchain } from "../../lib/api";

/**
 * OnchainSection — admin editor for the on-chain feature flag.
 *
 * Nothing on-chain ships today; this panel lets ops configure the rollout
 * (label, description, treasury wallet, per-feature sub-flags) so the
 * marketing surface and future smart-contract endpoints can pick up the
 * config without a code deploy.
 */
export function OnchainSection({ onToast }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminGetOnchain().then(setState).catch((e) => {
      onToast?.(e?.response?.data?.detail || "Couldn't load on-chain config.");
    });
  }, []); // eslint-disable-line

  if (!state) return <p className="text-white/45 text-sm">Loading…</p>;

  const setField = (k, v) => setState((s) => ({ ...s, [k]: v }));
  const setFeat  = (k, v) => setState((s) => ({ ...s, features: { ...s.features, [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      const fresh = await adminSetOnchain({
        enabled: state.enabled,
        label: state.label,
        description: state.description,
        network: state.network,
        treasury_wallet: state.treasury_wallet || "",
        features: state.features,
      });
      setState(fresh);
      onToast?.("On-chain config saved.");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't save.");
    } finally { setBusy(false); }
  };

  const FEAT_ROWS = [
    ["token_drops",     "Token drops",       "Award SPL tokens for league wins."],
    ["nft_holo_cards",  "NFT Holo Cards",    "Mint Holo Player Cards on-chain."],
    ["wallet_login",    "Wallet sign-in",    "Sign in with a Solana wallet (Phantom, Solflare)."],
    ["prize_payouts",   "Prize payouts",     "Send tournament prize pools as on-chain transfers."],
  ];

  return (
    <section data-testid="admin-onchain-section" className="space-y-5">
      <div className={`rounded-2xl p-4 border flex items-center gap-3 flex-wrap ${
        state.enabled
          ? "border-[#14b8a6]/40 bg-[#14b8a6]/8"
          : "border-white/15 bg-white/5"
      }`}>
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-link mr-1" />ON-CHAIN FEATURES · CURRENTLY ON HOLD
        </p>
        <span data-testid="admin-onchain-status"
              className={`text-[10px] tracking-widest px-2 py-1 rounded-md font-bold ${
                state.enabled
                  ? "bg-[#14b8a6]/20 text-[#14b8a6] border border-[#14b8a6]/40"
                  : "bg-white/10 text-white/60 border border-white/15"
              }`}>
          {state.enabled ? "● ENABLED" : "○ DISABLED"}
        </span>
        <span className="text-[10px] text-white/50 ml-auto">
          No on-chain calls happen yet — this is the config holding pen.
        </span>
      </div>

      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={!!state.enabled}
          onChange={(e) => setField("enabled", e.target.checked)}
          data-testid="admin-onchain-toggle"
        />
        <span className="text-sm text-white">
          Enable on-chain features platform-wide (surfaces the panel on pricing + dashboards)
        </span>
      </label>

      <div className="grid lg:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-[10px] tracking-widest text-[#a3a3ac]">LABEL</span>
          <input
            value={state.label || ""}
            onChange={(e) => setField("label", e.target.value)}
            maxLength={80}
            data-testid="admin-onchain-label"
            className="mt-1 w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
          />
        </label>
        <label className="block">
          <span className="text-[10px] tracking-widest text-[#a3a3ac]">NETWORK</span>
          <select
            value={state.network || "solana_devnet"}
            onChange={(e) => setField("network", e.target.value)}
            data-testid="admin-onchain-network"
            className="mt-1 w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
          >
            <option value="solana_devnet">solana_devnet</option>
            <option value="solana_testnet">solana_testnet</option>
            <option value="solana_mainnet">solana_mainnet</option>
          </select>
        </label>
      </div>

      <label className="block">
        <span className="text-[10px] tracking-widest text-[#a3a3ac]">DESCRIPTION (PUBLIC)</span>
        <textarea
          rows={3}
          maxLength={600}
          value={state.description || ""}
          onChange={(e) => setField("description", e.target.value)}
          data-testid="admin-onchain-description"
          className="mt-1 w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
        />
      </label>

      <label className="block">
        <span className="text-[10px] tracking-widest text-[#a3a3ac]">TREASURY WALLET (PRIVATE)</span>
        <input
          value={state.treasury_wallet || ""}
          onChange={(e) => setField("treasury_wallet", e.target.value)}
          maxLength={64}
          placeholder="Solana pubkey, base58"
          data-testid="admin-onchain-treasury"
          className="mt-1 w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm font-mono"
        />
        <span className="block text-[10px] text-white/45 mt-1">
          Never returned by the public /settings/onchain endpoint.
        </span>
      </label>

      <div className="rounded-2xl p-4 bg-black/30 border border-white/10">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-3">
          <i className="fas fa-toggle-on mr-1" />SUB-FEATURES
        </p>
        <ul className="space-y-2">
          {FEAT_ROWS.map(([key, label, hint]) => (
            <li key={key} className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={!!state.features?.[key]}
                onChange={(e) => setFeat(key, e.target.checked)}
                data-testid={`admin-onchain-feat-${key}`}
                className="mt-1"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white">{label}</p>
                <p className="text-[11px] text-white/45">{hint}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <button
        type="button" onClick={save} disabled={busy}
        data-testid="admin-onchain-save-btn"
        className="text-[11px] tracking-widest px-5 py-2 rounded-xl btn-neon disabled:opacity-50"
      >
        {busy ? "…" : <><i className="fas fa-floppy-disk mr-1" />SAVE ON-CHAIN CONFIG</>}
      </button>
    </section>
  );
}
